import { Box, SxProps, Theme, useTheme } from "@mui/material";
import { ExpandableTable, RoundedBox } from "@knowfully/ui.common";
import {
  DEFAULT_ROWS_PER_PAGE,
  LazyTableRow,
  Severity,
  SnackbarMessageProps,
  snackbarStore,
  TableCellType,
  TableSortType,
} from "@knowfully/ui.common";
import UserCell from "components/UserCell";
import useGlobalContext from "hooks/globalData";
import { useEffect, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  CompletionsFilter,
  ListRequestOfCompletionsFilter,
  OrganizationCompletion,
} from "services/organizationPortalClient";
import CertificatesActions from "./CertificatesActions";
import CertificatesExpandedInfo from "./CertificatesExpandedInfo";
import CertificatesSearchFilter from "./CertificatesSearchFilter";
import CertificatesThreeDotsMenu, {
  ExamResultsDialogRequest,
} from "./CertificatesThreeDotsMenu";
import ExamResultsDialog from "./Dialogs/ExamResultsDialog";
import contextStore from "stores/contextStore";

const useStyles = (theme: Theme): { [key: string]: SxProps } => ({
  title: {
    fontSize: "1.2em",
    color: theme.palette.primary.main,
    fontWeight: "bold",
    mb: 2,
  },
});

type ActionsCellProps = {
  value: OrganizationCompletion;
  onExamResults: (request: ExamResultsDialogRequest) => void;
  onDownloadAll: (completionId: string) => void;
};

function ActionsCell({
  value,
  onExamResults,
  onDownloadAll,
}: Readonly<ActionsCellProps>) {
  return (
    <CertificatesThreeDotsMenu
      completion={value}
      onExamResults={onExamResults}
      onDownloadAll={onDownloadAll}
    />
  );
}

const getCells = (
  t: ReturnType<typeof useIntl>["formatMessage"],
  onExamResults: (request: ExamResultsDialogRequest) => void,
  onDownloadAll: (completionId: string) => void
): Array<TableCellType> => [
  {
    accessor: "completionDate",
    type: "date",
    label: t({ id: "completionDate" }),
  },
  {
    accessor: "user",
    customSortAccessor: "lastName",
    type: "node",
    label: t({ id: "user" }),
    View: ({ value }: { value: OrganizationCompletion }) => (
      <UserCell
        firstName={value.firstName}
        lastName={value.lastName}
        email={value.email}
      />
    ),
  },
  {
    accessor: "courseTitle",
    type: "text",
    label: t({ id: "courseTitle" }),
  },
  {
    accessor: "skuFormat.name",
    customSortAccessor: "skuFormatId",
    type: "node",
    label: t({ id: "format" }),
    View: ({ value }: { value: OrganizationCompletion }) => (
      <div>{value.skuFormat?.name}</div>
    ),
  },
  {
    accessor: "",
    type: "node",
    label: "",
    noSort: true,
    align: "right",
    View: ({ value }: { value: OrganizationCompletion }) => (
      <ActionsCell
        value={value}
        onExamResults={onExamResults}
        onDownloadAll={onDownloadAll}
      />
    ),
  },
];

export default function Certificates() {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { certificatesService } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("completionDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [filter, setFilter] = useState({} as CompletionsFilter);
  const [examResultsDialog, setExamResultsDialog] =
    useState<ExamResultsDialogRequest>();
  const { organization } = contextStore.useState();

  const showDownloadError = () => {
    snackbarStore.update((s) => {
      s.snackbarMessage = new SnackbarMessageProps(
        t({ id: "downloadCertificatesError" }),
        Severity.Error
      );
    });
  };

  const handleDownloadAll = async (completionId: string) => {
    const downloadWindow = globalThis.open("", "_blank");

    if (!downloadWindow) {
      showDownloadError();
      return;
    }

    downloadWindow.opener = null;

    try {
      const url = await certificatesService.getCertificatesDownloadUrl(
        completionId
      );
      downloadWindow.location.href = url;
    } catch {
      downloadWindow.close();
      showDownloadError();
    }
  };

  const { data, isFetching } = useQuery(
    [
      "completions",
      organization?.id,
      order,
      orderBy,
      page,
      rowsPerPage,
      filter,
    ],
    () =>
      certificatesService.getCompletions(
        ListRequestOfCompletionsFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: filter,
        })
      ),
    { keepPreviousData: true }
  );

  useEffect(() => {
    setPage(0);
  }, [filter]);

  const cells = getCells(t, setExamResultsDialog, handleDownloadAll);

  const certificates = (data?.data ?? []).map(
    (certificate) =>
      ({
        ...certificate,
        isExpandable: true,
        renderExpandedData: () => (
          <CertificatesExpandedInfo value={certificate} />
        ),
      }) as LazyTableRow
  );

  return (
    <Box>
      <Box sx={styles.title}>
        <T id="certificates" />
      </Box>
      <RoundedBox>
        <CertificatesSearchFilter onChange={setFilter} />
      </RoundedBox>
      <RoundedBox>
        <ExpandableTable
          {...{
            "aria-label": t({ id: "certificates" }),
            rowCount: data?.totalRecords ?? 0,
            rows: certificates,
            loading: isFetching,
            cells,
            page,
            setPage,
            order,
            setOrder,
            orderBy,
            setOrderBy,
            rowsPerPage,
            setRowsPerPage,
            titleComponent: (
              <CertificatesActions
                filter={filter}
                sortDescending={order === "desc"}
                sortField={orderBy}
              />
            ),
            collapsable: true,
          }}
        />
      </RoundedBox>
      <ExamResultsDialog
        open={!!examResultsDialog}
        onClose={() => setExamResultsDialog(undefined)}
        completionId={examResultsDialog?.completionId}
        courseTitle={examResultsDialog?.courseTitle}
      />
    </Box>
  );
}
